import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, MessageSquare, Mail, X, ArrowRight, Send } from "lucide-react";
import { useCompany } from "../../context/CompanyContext";

// The luxury "Get In Touch" contact modal.
// Actions are wired to the centralized company_contact settings.
const WA_ICON = (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden>
    <path d="M20.52 3.48A11.94 11.94 0 0 0 12.07 0C5.5 0 .17 5.33.17 11.9c0 2.1.55 4.15 1.6 5.96L0 24l6.32-1.65a11.9 11.9 0 0 0 5.75 1.47h.01c6.57 0 11.9-5.33 11.9-11.9 0-3.18-1.24-6.17-3.46-8.44ZM12.07 21.8h-.01a9.87 9.87 0 0 1-5.03-1.38l-.36-.21-3.75.98 1-3.66-.24-.38a9.87 9.87 0 0 1-1.5-5.25c0-5.46 4.44-9.9 9.9-9.9 2.65 0 5.13 1.03 7 2.9a9.85 9.85 0 0 1 2.9 7c-.01 5.47-4.45 9.9-9.91 9.9Zm5.44-7.42c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.6-.92-2.2-.24-.58-.49-.5-.67-.5l-.57-.01c-.2 0-.52.07-.8.37-.27.3-1.05 1.02-1.05 2.5 0 1.47 1.07 2.89 1.22 3.09.15.2 2.1 3.2 5.08 4.49.71.3 1.26.48 1.7.62.71.23 1.36.2 1.87.12.57-.08 1.76-.72 2.01-1.42.25-.7.25-1.3.17-1.42-.07-.12-.27-.2-.57-.35Z"/>
  </svg>
);

const ActionRow = ({ icon: Icon, label, sub, onClick, href, testId, iconNode }) => {
  const Cmp = href ? "a" : "button";
  const props = href ? { href } : { onClick, type: "button" };
  return (
    <Cmp
      {...props}
      data-testid={testId}
      className="group w-full text-left flex items-center gap-4 px-4 py-3 rounded-xl border border-[#d6d40022] bg-[#0b0b0b] hover:border-[#d6d40066] hover:bg-[#111] transition-all"
    >
      <span className="h-10 w-10 shrink-0 rounded-lg flex items-center justify-center bg-[#d6d40012] text-[#d6d400] border border-[#d6d40033] group-hover:bg-[#d6d40022]">
        {iconNode || <Icon size={16} />}
      </span>
      <span className="flex-1 min-w-0">
        <span className="block font-display font-bold uppercase tracking-[0.14em] text-xs text-[#f4f4ea]">{label}</span>
        {sub && <span className="block font-body text-[13px] text-[#c9c9ba] mt-0.5 truncate">{sub}</span>}
      </span>
      <ArrowRight size={14} className="text-[#8f9080] group-hover:text-[#d6d400] transition-colors" />
    </Cmp>
  );
};

export default function ContactModal() {
  const { company, isOpen, closeContactModal } = useCompany();
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && closeContactModal();
    if (isOpen) {
      document.body.style.overflow = "hidden";
      window.addEventListener("keydown", onKey);
    }
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [isOpen, closeContactModal]);

  if (!company) return null;
  const tel = company.phone_e164 || company.phone || "";
  const sms = company.sms_e164 || company.sms_number || tel;
  const wa = (company.whatsapp_number || "").replace(/[^\d+]/g, "");

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 z-[90] flex flex-col items-end overflow-y-auto overscroll-contain px-4 sm:px-6"
          data-lenis-prevent
          role="dialog"
          aria-modal="true"
          aria-label="Contact VOOR BLACK"
        >
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="absolute inset-0 bg-black/80 backdrop-blur-md"
            data-testid="contact-modal-overlay"
            onClick={closeContactModal}
          />
          <motion.div
            initial={{ y: 24, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 24, opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
            className="relative z-[1] my-auto w-full shrink-0 sm:w-[440px] rounded-2xl border border-[#d6d40033] bg-[#050505] shadow-[0_30px_120px_rgba(214,212,0,0.15)]"
            data-testid="contact-modal"
          >
            <div className="px-6 pt-6 pb-5 border-b border-[#d6d40018]">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-body text-[10px] tracking-[0.28em] uppercase text-[#8f9080]">Concierge Line</div>
                  <h2 className="font-display font-bold text-[#d6d400] text-lg sm:text-xl uppercase tracking-[0.18em] mt-1">
                    Get In Touch
                  </h2>
                  <div className="font-body text-[13px] text-[#c9c9ba] mt-1.5">
                    Available 24 / 7 · Discretion guaranteed
                  </div>
                </div>
                <button onClick={closeContactModal} data-testid="contact-modal-close"
                  className="text-[#8f9080] hover:text-[#d6d400] p-1" aria-label="Close">
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="p-5 space-y-2.5">
              {tel && (
                <ActionRow icon={Phone} label="Call Now" sub={company.phone}
                  href={`tel:${tel}`} testId="contact-modal-call" />
              )}
              {sms && (
                <ActionRow icon={MessageSquare} label="Text Us" sub={company.sms_number || company.phone}
                  href={`sms:${sms}`} testId="contact-modal-text" />
              )}
              {wa && (
                <ActionRow iconNode={WA_ICON} label="WhatsApp" sub={company.whatsapp_number}
                  href={`https://wa.me/${wa.replace(/^\+/, "")}`} testId="contact-modal-whatsapp" />
              )}
              {company.primary_email && (
                <ActionRow icon={Mail} label="Primary Email" sub={company.primary_email}
                  href={`mailto:${company.primary_email.trim()}`}
                  testId="contact-modal-email-primary" />
              )}
              {company.secondary_email && (
                <ActionRow icon={Mail} label="Contract and Partnership" sub={company.secondary_email}
                  href={`mailto:${company.secondary_email.trim()}`}
                  testId="contact-modal-email-secondary" />
              )}
              <ActionRow icon={Send} label="Book A Ride" sub="Start your reservation"
                onClick={() => { closeContactModal(); navigate(company.booking_url || "/reserve"); }}
                testId="contact-modal-book" />
            </div>
            <div className="px-6 py-4 border-t border-[#d6d40018] flex items-center justify-between gap-3">
              <button
                onClick={() => { closeContactModal(); navigate("/contact"); }}
                data-testid="contact-modal-open-page"
                className="font-body uppercase tracking-[0.14em] text-[11px] text-[#c9c9ba] hover:text-[#d6d400] transition-colors"
              >
                Full Contact Page →
              </button>
              <span className="font-body text-[10px] tracking-[0.2em] uppercase text-[#6a6b5e]">Time Is Wealth</span>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
